import { useState, useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/auth/context'
import { supabase } from '@/lib/supabase'
import { isTester } from '@/lib/api/admin'

// Full-page loading screen with branded compass and dark/light mode support
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center">
      <div className="w-20 h-20 rounded-[22%] bg-black overflow-hidden shadow-xl shadow-amber-500/10 ring-1 ring-white/10 mb-6">
        <img src="/app-logo.png" alt="" className="w-full h-full object-contain" draggable={false} />
      </div>
      <div className="w-6 h-6 rounded-full border-2 border-amber-500/30 border-t-amber-500 animate-spin" />
      <p className="mt-4 text-sm text-slate-400 dark:text-slate-500">Loading...</p>
    </div>
  )
}

// Protect routes that require authentication
export function AuthGuard() {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return <LoadingScreen />
  }

  if (!user) {
    // Redirect to login, but save the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <Outlet />
}

// Session storage key for onboarding completion
const ONBOARDING_COMPLETE_KEY = 'miac_onboarding_complete'

// Check if user needs onboarding (used by main app routes)
export function OnboardingGuard() {
  const { user, session, loading } = useAuth()
  const location = useLocation()
  const [checkingOnboarding, setCheckingOnboarding] = useState(true)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user || !session) {
        setCheckingOnboarding(false)
        return
      }

      // Check sessionStorage first - if marked complete in this session, skip the API call
      const cachedUserId = sessionStorage.getItem(ONBOARDING_COMPLETE_KEY)
      if (cachedUserId === user.id) {
        setNeedsOnboarding(false)
        setCheckingOnboarding(false)
        return
      }

      // Retry logic with delay to handle race conditions
      const maxRetries = 3
      const retryDelay = 500 // ms
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          // Use direct fetch with USER'S JWT token for proper RLS authentication
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
          const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
          
          const response = await fetch(
            `${supabaseUrl}/rest/v1/user_profiles?select=id,onboarding_completed_at&id=eq.${user.id}`,
            {
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${session.access_token}`, // Use user's JWT, NOT anon key!
              }
            }
          )
          
          if (!response.ok) {
            console.error('[OnboardingGuard] Response not OK:', response.status, response.statusText)
            const errorText = await response.text()
            console.error('[OnboardingGuard] Error body:', errorText)
            // If unauthorized or forbidden, might be a token issue - don't retry
            if (response.status === 401 || response.status === 403) {
              break
            }
            // Retry on other errors
            if (attempt < maxRetries - 1) {
              await new Promise(resolve => setTimeout(resolve, retryDelay))
              continue
            }
          }
          
          const data = await response.json()
          const profile = data?.[0]
          
          // If profile doesn't exist yet, wait and retry (profile creation might be in progress)
          if (!profile && attempt < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, retryDelay))
            continue
          }
          
          const needsOnboarding = !profile?.onboarding_completed_at
          
          // Cache completion in sessionStorage to avoid repeated API calls
          if (!needsOnboarding) {
            sessionStorage.setItem(ONBOARDING_COMPLETE_KEY, user.id)
          }
          
          setNeedsOnboarding(needsOnboarding)
          setCheckingOnboarding(false)
          return // Success, exit the retry loop
          
        } catch (err) {
          console.warn('[OnboardingGuard] Query failed:', err)
          if (attempt < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, retryDelay))
          }
        }
      }
      
      // All retries failed - allow access to avoid blocking the user
      console.warn('[OnboardingGuard] All retries failed, allowing access')
      setNeedsOnboarding(false)
      setCheckingOnboarding(false)
    }

    if (!loading && user && session) {
      checkOnboarding()
    } else if (!loading) {
      setCheckingOnboarding(false)
    }
  }, [user, session, loading])

  if (loading || checkingOnboarding) {
    return <LoadingScreen />
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Redirect to welcome if user hasn't completed onboarding
  if (needsOnboarding && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />
  }

  return <Outlet />
}

// Redirect authenticated users away from login/public pages
export function GuestGuard() {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return <LoadingScreen />
  }

  if (user) {
    // Redirect to the page they were trying to access, or home
    const from = (location.state as { from?: Location })?.from?.pathname || '/'
    return <Navigate to={from} replace />
  }

  return <Outlet />
}

// Protect routes that require tester access (animation testing)
export function TesterGuard() {
  const { user, loading } = useAuth()
  const [checkingTester, setCheckingTester] = useState(true)
  const [isTesterUser, setIsTesterUser] = useState(false)

  useEffect(() => {
    const checkTester = async () => {
      if (!user) {
        setCheckingTester(false)
        return
      }

      const result = await isTester()
      setIsTesterUser(result)
      setCheckingTester(false)
    }

    if (!loading && user) {
      checkTester()
    } else if (!loading) {
      setCheckingTester(false)
    }
  }, [user, loading])

  if (loading || checkingTester) {
    return <LoadingScreen />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Silently redirect non-testers to home
  if (!isTesterUser) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}
